import React, { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  LockKey,
  MagnifyingGlass,
  DownloadSimple,
  ArrowLeft,
  Crown,
  Users,
  EnvelopeSimple,
  Phone,
  MapPin,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fetchLeads } from "@/lib/api";

const PASSCODE_KEY = "wff_admin_pass";

export default function AdminLeads() {
  const navigate = useNavigate();
  const [passcode, setPasscode] = useState(localStorage.getItem(PASSCODE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async (code) => {
    setLoading(true);
    try {
      const data = await fetchLeads(code);
      setLeads(data.leads || []);
      setAuthed(true);
      localStorage.setItem(PASSCODE_KEY, code);
      toast.dismiss();
    } catch (e) {
      setAuthed(false);
      if (e?.response?.status === 401) {
        localStorage.removeItem(PASSCODE_KEY);
        toast.error("Invalid passcode");
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem(PASSCODE_KEY);
    if (saved) load(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) =>
      [l.name, l.email, l.phone, l.city].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [leads, search]);

  const exportCsv = () => {
    const rows = [["Name", "Email", "Phone", "City", "Premium", "Verified At", "Calendar Downloads"]];
    filtered.forEach((l) =>
      rows.push([l.name || "", l.email || "", l.phone || "", l.city || "", l.premium ? "Yes" : "No", l.verified_at || "", l.calendar_downloads || 0])
    );
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wff-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} leads`);
  };

  const premiumCount = leads.filter((l) => l.premium).length;

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0D17] px-5">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          data-testid="admin-login"
          className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#12141F] p-8"
        >
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500/15 ring-1 ring-blue-500/30">
            <LockKey size={24} weight="duotone" className="text-blue-400" />
          </div>
          <h1 className="font-display text-xl font-bold tracking-tight text-white">Leads Dashboard</h1>
          <p className="mt-1 text-sm text-slate-400">Enter the admin passcode to continue.</p>
          <Input
            data-testid="admin-passcode-input"
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(passcode)}
            placeholder="Admin passcode"
            className="mt-5 h-12 border-white/10 bg-white/[0.04] text-white placeholder:text-slate-400 focus-visible:ring-blue-500"
          />
          <Button
            data-testid="admin-login-btn"
            onClick={() => load(passcode)}
            disabled={loading || !passcode}
            className="mt-4 h-12 w-full rounded-full bg-blue-600 font-semibold text-white hover:bg-blue-500"
          >
            {loading ? "Checking…" : "Unlock Dashboard"}
          </Button>
          <button
            onClick={() => navigate("/")}
            className="mx-auto mt-5 flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-white"
          >
            <ArrowLeft size={15} /> Back to site
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0D17]">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0B0D17]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="text-slate-400 transition-colors hover:text-white">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="font-display text-lg font-bold tracking-tight text-white">Leads Dashboard</h1>
              <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">World Franchise Forum</p>
            </div>
          </div>
          <Button
            data-testid="export-csv-btn"
            onClick={exportCsv}
            className="rounded-full bg-blue-600 font-semibold text-white hover:bg-blue-500"
          >
            <DownloadSimple size={17} className="mr-1.5" /> Export CSV
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard icon={<Users size={20} weight="duotone" className="text-blue-400" />} label="Total Leads" value={leads.length} />
          <StatCard icon={<Crown size={20} weight="fill" className="text-amber-300" />} label="Premium" value={premiumCount} />
          <StatCard icon={<MagnifyingGlass size={20} className="text-emerald-400" />} label="Showing" value={filtered.length} />
        </div>

        <div className="relative mb-5">
          <MagnifyingGlass size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="lead-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, phone or city…"
            className="h-12 rounded-full border-white/10 bg-white/[0.04] pl-11 text-white placeholder:text-slate-500 focus-visible:ring-blue-500"
          />
        </div>

        <div className="overflow-hidden rounded-2xl border border-white/10" data-testid="leads-table">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-white/[0.04] text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Phone</Th>
                  <Th>City</Th>
                  <Th>Plan</Th>
                  <Th>Calendar PDF</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center text-slate-500">
                      No leads match your search.
                    </td>
                  </tr>
                ) : (
                  filtered.map((l, i) => (
                    <tr key={l.email || i} data-testid={`lead-row-${i}`} className="transition-colors hover:bg-white/[0.03]">
                      <td className="px-5 py-4 font-medium text-white">{l.name || <span className="text-slate-600">—</span>}</td>
                      <td className="px-5 py-4 text-slate-300">
                        <span className="inline-flex items-center gap-1.5"><EnvelopeSimple size={14} className="text-slate-500" />{l.email}</span>
                      </td>
                      <td className="px-5 py-4 text-slate-300">
                        {l.phone ? <span className="inline-flex items-center gap-1.5"><Phone size={14} className="text-slate-500" />{l.phone}</span> : <span className="text-slate-600">—</span>}
                      </td>
                      <td className="px-5 py-4 text-slate-300">
                        {l.city ? <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-slate-500" />{l.city}</span> : <span className="text-slate-600">—</span>}
                      </td>
                      <td className="px-5 py-4">
                        {l.premium ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 text-xs font-semibold text-amber-300">
                            <Crown size={12} weight="fill" /> Premium
                          </span>
                        ) : (
                          <span className="rounded-full bg-white/5 px-2.5 py-1 text-xs text-slate-400">Free</span>
                        )}
                      </td>
                      <td data-testid={`lead-calendar-${i}`} className="px-5 py-4 text-slate-300">
                        {l.calendar_downloads ? (
                          <span className="rounded-full bg-blue-500/10 px-2.5 py-1 text-xs font-semibold text-blue-300">
                            Downloaded ×{l.calendar_downloads}
                          </span>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

const Th = ({ children }) => <th className="px-5 py-3.5 font-semibold">{children}</th>;

const StatCard = ({ icon, label, value }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
    <div className="flex items-center gap-2">{icon}<p className="text-xs uppercase tracking-wider text-slate-500">{label}</p></div>
    <p className="mt-2 font-display text-3xl font-extrabold text-white">{value}</p>
  </div>
);
